import { useMemo, useCallback, useEffect } from 'react';
import { createStoreWithSubscribe } from '@frontend/store';
import { ValidChannel, ChannelPayload, QueueEventAction } from './types/types';

// =============================================================================
// CORE EVENT STRUCTURE
// =============================================================================

// Event structure with type safety
export interface QueueEvent<T = any> {
  id: string;
  channel: string;
  payload: T;
  timestamp: number;
  priority?: 'low' | 'normal' | 'high';
  waitForHandler?: boolean; // If true, keep event in queue until there's a handler
  action?: QueueEventAction<T>; // Optional action to run for this event
}

// Event handler function type
export type EventHandler<T = any> = (event: QueueEvent<T>) => Promise<boolean> | boolean;

// Subscription info
export interface Subscription {
  id: string;
  handler: EventHandler;
}

// Queue state with typed channels
interface QueueState {
  channels: Record<string, QueueEvent[]>;
  subscriptions: Record<string, Subscription[]>; // channel -> handlers
  processing: Set<string>; // event IDs currently being processed
}

// Queue actions with type safety
interface QueueActions {
  // Channel management
  enqueue: <T extends ValidChannel>(
    channel: T,
    event: Omit<QueueEvent<ChannelPayload<T>>, 'id' | 'timestamp' | 'channel'>
  ) => string;

  // Subscription management
  subscribe: (channel: string, handler: EventHandler) => string;
  unsubscribe: (channel: string, subscriptionId: string) => void;

  // Processing
  processChannel: (channel: string) => Promise<void>;
  processEvent: (eventId: string, channel: string) => Promise<boolean>;

  // Utilities
  getChannelEvents: <T extends ValidChannel>(channel: T) => QueueEvent<ChannelPayload<T>>[];
  getChannelCount: (channel: string) => number;
  clearChannel: (channel: string) => void;

  // Debug/monitoring
  getAllChannels: () => string[];
  getProcessingEvents: () => string[];
}

type QueueStore = QueueState & QueueActions;

// Utility to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Create the queue store
export const useQueueStore = createStoreWithSubscribe<QueueStore>(
  (set, get) => ({
    // Initial state
    channels: {},
    subscriptions: {},
    processing: new Set(),

    // Type-safe enqueue for registry channels
    enqueue: (channel, eventData) => {
      const eventId = generateId();
      const event: QueueEvent<ChannelPayload<typeof channel>> = {
        id: eventId,
        channel,
        timestamp: Date.now(),
        priority: 'normal',
        ...eventData,
      };

      set(
        (state) => {
          // Initialize channel if it doesn't exist
          if (!state.channels[channel]) {
            state.channels[channel] = [];
          }

          // Add event and sort by priority and timestamp
          const currentEvents = state.channels[channel] || [];
          const events = [...currentEvents, event];

          // Sort by priority (high -> normal -> low) then by timestamp
          events.sort((a, b) => {
            const priorityOrder: Record<string, number> = { high: 0, normal: 1, low: 2 };
            const aPriority = priorityOrder[a.priority || 'normal'];
            const bPriority = priorityOrder[b.priority || 'normal'];

            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }
            return a.timestamp - b.timestamp;
          });

          state.channels[channel] = events;
        },
        false,
        'ENQUEUE'
      );

      // Auto-process the channel immediately
      // setTimeout is used to allow the state update to complete before processing, deferring the processing to the next tick
      setTimeout(() => get().processChannel(channel), 0);

      return eventId;
    },

    // Subscribe to a channel
    subscribe: (channel, handler) => {
      const subscriptionId = generateId();

      set(
        (state) => {
          if (!state.subscriptions[channel]) {
            state.subscriptions[channel] = [];
          }

          state.subscriptions[channel].push({
            id: subscriptionId,
            handler,
          });
        },
        false,
        'SUBSCRIBE'
      );

      // Auto-process channel after subscribing to pick up any waiting events
      // setTimeout is used to allow the state update to complete before processing, deferring the processing to the next tick
      setTimeout(() => get().processChannel(channel), 0);

      return subscriptionId;
    },

    // Unsubscribe from a channel
    unsubscribe: (channel, subscriptionId) => {
      set(
        (state) => {
          if (state.subscriptions[channel]) {
            state.subscriptions[channel] = state.subscriptions[channel].filter((sub) => sub.id !== subscriptionId);

            // Clean up empty subscription arrays
            if (state.subscriptions[channel].length === 0) {
              delete state.subscriptions[channel];
            }
          }
        },
        false,
        'UNSUBSCRIBE'
      );
    },

    // Process all events in a channel
    processChannel: async (channel) => {
      const state = get();
      const events = state.channels[channel] || [];

      if (events.length === 0) return;

      // Process events one by one (could be made concurrent if needed)
      for (const event of events) {
        if (!state.processing.has(event.id)) {
          await get().processEvent(event.id, channel);
        }
      }
    },

    // Process a specific event
    processEvent: async (eventId, channel) => {
      const state = get();
      const event = state.channels[channel]?.find((e) => e.id === eventId);

      if (!event || state.processing.has(eventId)) {
        return false;
      }

      // Mark as processing
      set(
        (state) => {
          state.processing.add(eventId);
        },
        false,
        'START_PROCESSING'
      );

      const handlers = state.subscriptions[channel] || [];
      let allSuccess = true;

      // Process with all handlers for this channel
      for (const subscription of handlers) {
        try {
          const result = await subscription.handler(event);
          if (!result) {
            allSuccess = false;
            console.warn(`Handler failed for event ${eventId}`, { event, subscription: subscription.id });
          }
        } catch (error) {
          allSuccess = false;
          console.error(`Handler threw error for event ${eventId}`, error, { event, subscription: subscription.id });
        }
      }

      // Remove from processing and from queue if successful
      set(
        (state) => {
          state.processing.delete(eventId);

          if (allSuccess && handlers.length > 0) {
            // Only remove if we successfully processed with actual handlers
            if (state.channels[channel]) {
              state.channels[channel] = state.channels[channel].filter((e) => e.id !== eventId);
            }
          } else if (handlers.length === 0) {
            // No handlers available
            if (event.waitForHandler) {
              // Keep event in queue, it's waiting for a handler
              console.log(`Event ${eventId} waiting for handler in channel "${channel}"`);
            } else {
              // Remove event, it doesn't want to wait
              console.warn(`No handlers for event ${eventId} in channel "${channel}", removing event`);
              if (state.channels[channel]) {
                state.channels[channel] = state.channels[channel].filter((e) => e.id !== eventId);
              }
            }
          }
          // If allSuccess is false and there were handlers, event stays in queue for retry
        },
        false,
        'END_PROCESSING'
      );

      return allSuccess;
    },

    // Get events for a typed channel
    getChannelEvents: (channel) => {
      return get().channels[channel] || [];
    },

    // Get event count for a channel
    getChannelCount: (channel) => {
      return get().channels[channel]?.length || 0;
    },

    // Clear all events in a channel
    clearChannel: (channel) => {
      set(
        (state) => {
          if (state.channels[channel]) {
            state.channels[channel] = [];
          }
        },
        false,
        'CLEAR_CHANNEL'
      );
    },

    // Get all channel names
    getAllChannels: () => {
      return Object.keys(get().channels);
    },

    // Get currently processing event IDs
    getProcessingEvents: () => {
      return Array.from(get().processing);
    },
  }),
  { name: 'QueueStore', trace: true }
);

// =============================================================================
// REACT HOOKS
// =============================================================================

export const useChannel = <T extends ValidChannel>(channelName: T) => {
  // Get stable references to store methods (these don't change)
  const enqueue = useQueueStore((state) => state.enqueue);
  const subscribe = useQueueStore((state) => state.subscribe);
  const unsubscribe = useQueueStore((state) => state.unsubscribe);
  const processChannel = useQueueStore((state) => state.processChannel);
  const clearChannel = useQueueStore((state) => state.clearChannel);

  // Get current events and count for this specific channel
  const events = useQueueStore((state) => state.getChannelEvents(channelName));
  const count = useQueueStore((state) => state.channels[channelName]?.length || 0);

  // Create stable methods using useCallback to prevent recreation on every render
  const stableEnqueue = useCallback(
    (event: Omit<QueueEvent<ChannelPayload<T>>, 'id' | 'timestamp' | 'channel'>) => enqueue(channelName, event),
    [channelName, enqueue]
  );

  const stableSubscribe = useCallback(
    (handler: EventHandler) => subscribe(channelName, handler),
    [channelName, subscribe]
  );

  const stableUnsubscribe = useCallback(
    (subscriptionId: string) => unsubscribe(channelName, subscriptionId),
    [channelName, unsubscribe]
  );

  const stableProcess = useCallback(() => processChannel(channelName), [channelName, processChannel]);

  const stableClear = useCallback(() => clearChannel(channelName), [channelName, clearChannel]);

  return useMemo(
    () => ({
      events,
      count,
      enqueue: stableEnqueue,
      subscribe: stableSubscribe,
      unsubscribe: stableUnsubscribe,
      process: stableProcess,
      clear: stableClear,
    }),
    [events, count, stableEnqueue, stableSubscribe, stableUnsubscribe, stableProcess, stableClear]
  );
};

/**
 * Hook for subscribing to queue events in React components.
 * This hook properly manages subscription lifecycle and prevents infinite re-renders.
 *
 * @param channelName - The channel to subscribe to
 * @param handler - The event handler function (should be wrapped in useCallback)
 * @param deps - Dependencies that should trigger re-subscription (optional)
 */
export const useChannelSubscription = <T extends ValidChannel>(
  channelName: T,
  handler: EventHandler<ChannelPayload<T>>,
  deps: any[] = []
) => {
  const subscribe = useQueueStore((state) => state.subscribe);
  const unsubscribe = useQueueStore((state) => state.unsubscribe);

  useEffect(() => {
    const subscriptionId = subscribe(channelName, handler);

    return () => {
      unsubscribe(channelName, subscriptionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, handler, subscribe, unsubscribe, ...deps]);
};

/**
 * Hook for conditionally subscribing to queue events in React components.
 * Only subscribes when the condition is true.
 *
 * @param channelName - The channel to subscribe to
 * @param handler - The event handler function (should be wrapped in useCallback)
 * @param condition - Only subscribe when this is true
 * @param deps - Dependencies that should trigger re-subscription (optional)
 */
export const useConditionalChannelSubscription = <T extends ValidChannel>(
  channelName: T,
  handler: EventHandler<ChannelPayload<T>>,
  condition: boolean,
  deps: any[] = []
) => {
  const subscribe = useQueueStore((state) => state.subscribe);
  const unsubscribe = useQueueStore((state) => state.unsubscribe);

  useEffect(() => {
    if (!condition) {
      return; // Don't subscribe if condition is false
    }

    const subscriptionId = subscribe(channelName, handler);

    return () => {
      unsubscribe(channelName, subscriptionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName, handler, condition, subscribe, unsubscribe, ...deps]);
};

export * from './utils/creators';
export * from './types/types';
